import { useCallback, useEffect, useRef, useState } from 'react';

import { useFilesService } from '../../../services/hooks';
import type { FileInputProps, FileInputValue } from '../FileInput.intefaces';

type UseFileInputDocumentOptions = Pick<
  FileInputProps,
  'value' | 'onChange' | 'withAuth'
>;

export const useFileInputDocument = ({
  value,
  onChange,
  withAuth,
}: UseFileInputDocumentOptions) => {
  const filesService = useFilesService();
  const blobDocument = useRef<Blob | null>(null);
  const [cacheFileName, setCacheFilename] = useState('');

  /**
   * Функция handleChange обрабатывает изменение значения компонента FileInput.
   * Она вызывает переданную функцию onChange с новым значением.
   * Если ранее было установлено cacheFileName, оно сбрасывается.
   *
   * @param nextValue - Новое значение компонента или undefined.
   */
  const handleChange = useCallback(
    (nextValue: FileInputValue | undefined) => {
      if (cacheFileName) setCacheFilename('');

      onChange(nextValue);
    },
    [onChange, cacheFileName],
  );

  useEffect(() => {
    blobDocument.current = null;
  }, [value]);

  /**
   * Функция load загружает документ в формате Blob.
   * Если документ уже загружен (blobDocument не null), возвращает его.
   * Если источник документа (value?.source) - строка, загружает файл из сервиса FilesService.
   * Устанавливает имя файла в кэше (cacheFileName) и документ (blobDocument).
   * Если источник документа - объект типа Blob, устанавливает его как blobDocument.
   * Возвращает загруженный или ранее установленный документ.
   *
   * @returns  - Обещание, которое разрешится в Blob документе.
   */
  const load: () => Promise<Blob> = useCallback(async () => {
    if (blobDocument.current) {
      return blobDocument.current;
    }

    if (typeof value?.source === 'string') {
      const fileData = await filesService.getFile(value.source, withAuth);
      const { file, filename } = await fileData;

      if (filename) {
        setCacheFilename(filename);
      }

      blobDocument.current = await file;
      return file;
    } else {
      if (value?.source) {
        blobDocument.current = value.source;
      }

      return value?.source || new Blob();
    }
  }, [filesService, value?.source, withAuth]);

  const onDocumentChange = useCallback((blob: Blob) => {
    blobDocument.current = blob;
  }, []);

  return { blobDocument, cacheFileName, handleChange, load, onDocumentChange };
};
